import React, { useEffect, useMemo, useRef } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import ImageResize from 'quill-image-resize-module-react';
import 'react-quill/dist/quill.snow.css'; // Quill theme

Quill.register('modules/imageResize', ImageResize);

const formats = [
  'header', 'bold', 'italic', 'underline', 'list', 'bullet', 'align', 'indent', 'clean', 'image', 'width', 'height', 'style'
];

const MAX_IMAGE_DIMENSION = 1024;
const JPEG_QUALITY = 0.85;
const INITIAL_DISPLAY_WIDTH = 240; // px — user can drag-resize larger if needed

const downscaleImage = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = (ev) => {
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      const longest = Math.max(width, height);
      if (longest > MAX_IMAGE_DIMENSION) {
        const scale = MAX_IMAGE_DIMENSION / longest;
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      // Keep PNG (sharp screenshot text, preserves transparency); re-encode others as JPEG.
      const mime = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
      resolve(canvas.toDataURL(mime, JPEG_QUALITY));
    };
    img.onerror = reject;
    img.src = ev.target.result;
  };
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const insertImageFile = async (editor, file) => {
  try {
    const dataUrl = await downscaleImage(file);
    const range = editor.getSelection(true);
    editor.insertEmbed(range.index, 'image', dataUrl, 'user');
    editor.formatText(range.index, 1, 'width', String(INITIAL_DISPLAY_WIDTH), 'user');
    editor.setSelection(range.index + 1, 0, 'user');
  } catch (err) {
    console.error('Image insert failed', err);
  }
};

export default function QuillEditor({value, onEditorChange}) {
  const quillRef = useRef(null);

  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, false] }],
        ['bold', 'italic', 'underline'],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        [{ 'align': [] }],
        [{ 'indent': '-1' }, { 'indent': '+1' }],
        ['image'],
        ['clean']
      ],
      handlers: {
        image: function () {
          const editor = quillRef.current?.getEditor();
          if (!editor) return;
          const input = document.createElement('input');
          input.type = 'file';
          input.accept = 'image/*';
          input.onchange = () => {
            const file = input.files && input.files[0];
            if (file) insertImageFile(editor, file);
          };
          input.click();
        }
      }
    },
    imageResize: {
      modules: ['Resize', 'DisplaySize', 'Toolbar']
    }
  }), []);

  useEffect(() => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;

    const handlePaste = (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.type && item.type.indexOf('image/') === 0) {
          e.preventDefault();
          const file = item.getAsFile();
          if (file) insertImageFile(editor, file);
          return;
        }
      }
    };

    const hasFiles = (dt) =>
      dt && dt.types && Array.prototype.indexOf.call(dt.types, 'Files') !== -1;

    const handleDragOver = (e) => {
      if (hasFiles(e.dataTransfer)) {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
      }
    };

    const handleDrop = (e) => {
      const files = e.dataTransfer?.files;
      if (!files || !files.length) return;
      const imageFiles = Array.from(files).filter((f) => f.type.indexOf('image/') === 0);
      if (!imageFiles.length) return;
      e.preventDefault();
      e.stopPropagation();
      // Move caret to drop point so the image inserts where the user dropped it.
      if (document.caretRangeFromPoint) {
        const caret = document.caretRangeFromPoint(e.clientX, e.clientY);
        if (caret) {
          const sel = window.getSelection();
          sel.removeAllRanges();
          sel.addRange(caret);
        }
      }
      imageFiles.forEach((file) => insertImageFile(editor, file));
    };

    editor.root.addEventListener('paste', handlePaste);
    editor.root.addEventListener('dragover', handleDragOver);
    editor.root.addEventListener('drop', handleDrop);
    return () => {
      editor.root.removeEventListener('paste', handlePaste);
      editor.root.removeEventListener('dragover', handleDragOver);
      editor.root.removeEventListener('drop', handleDrop);
    };
  }, []);

  return (
    <div>
      <ReactQuill
        ref={quillRef}
        value={value}
        onChange={onEditorChange}
        modules={modules}
        formats={formats}
        className="quill-editor"
        theme="snow"
      />
    </div>
  );
}
